import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { ReportSharingMetadata } from '../../../models/reports-refactored.model';
import { getEmptyStatePlaceholderImage } from '../../utils/branded-placeholder.util';
import { computeReorderAnchor } from './reorder-anchor';
import {
  normalizePpeStatus,
  PpeDetectionStatus,
  ReportPpeStatusItem,
} from '../../../services/PPE/ppe.models';

export interface ReportData {
  report_id: string;
  short_id: string;
  report_name: string;
  published_at: string;
  published_by: string;
  report_type: string;
  project_id: string;
  project_name?: string;
  project_number?: string | null;
  camera_serial_numbers?: string[];
  status: string;
  report_floors: any[];
  is_merged: boolean;
  merged_at?: string;
  failed_report_floors_count: number;
  sharing?: ReportSharingMetadata;
  is_cloned?: boolean;
  has_deleted_sync_items?: boolean;
  is_unavailable_for_sync?: boolean;
  /** PPE auto-detection (V2CORE-2901) display status; absent on an older backend. */
  ppe_detection_status?: PpeDetectionStatus | string | null;
  /** AI observation count from the batch status endpoint (shown on Detected chips). */
  ppe_ai_observations_count?: number | null;
}

/** The PPE fields of a report row, as the table and the details panel show them. */
export interface ReportPpeRowState {
  ppe_detection_status: PpeDetectionStatus;
  ppe_ai_observations_count: number | null;
}

/**
 * Maps one item of `POST v2/reports/ppe-detection-status` to the row fields.
 * Returns null for an unknown / not viewable capture, so the caller keeps what it had.
 * A capture whose stored status is still NOT_DETECTED while some floor was sent and has
 * no answer yet shows as in progress (same rule as the backend rollup).
 */
export function ppeRowStateFromItem(
  item: ReportPpeStatusItem | null | undefined,
): ReportPpeRowState | null {
  if (!item || item.not_found || !item.status) {
    return null;
  }
  let status = normalizePpeStatus(item.status);
  if (status === 'NOT_DETECTED' && (item.floors?.in_progress ?? 0) > 0) {
    status = 'DETECTION_IN_PROGRESS';
  }
  const count = Number(item.ai_observations_count);
  return {
    ppe_detection_status: status,
    ppe_ai_observations_count: Number.isFinite(count) ? count : null,
  };
}

export interface SharedReportTableData {
  id: string;
  report_id: string;
  project_id: string;
  report_name: string;
  report_type: string;
  source_organization: string | null;
  shared_by_name: string | null;
  shared_at: string | null;
  floors_count: number;
  points_count: number;
  status: string;
}

type TableRowData = ReportData | SharedReportTableData;

export interface ReportMetaData {
  total_items: number;
  per_page: number;
  current_page: number;
  last_page?: number;
}

export interface ReportDroppedEvent {
  reportId: string;
  afterReportId: string | null;
  beforeReportId: string | null;
}

export const DRAG_HANDLE_COLUMN = 'drag_handle';
export const PPE_STATUS_COLUMN = 'ppe_status';
export const SELECT_COLUMN = 'select';

export enum REPORT_STATUS {
  PUBLISHED = 'PUBLISHED',
  PARTIALLY_PUBLISHED = 'PARTIALLY_PUBLISHED',
}

@Component({
  selector: 'app-shared-360-reports-table',
  templateUrl: './shared-360-reports-table.component.html',
  styleUrls: ['./shared-360-reports-table.component.scss'],
})
export class Shared360ReportsTableComponent implements OnInit, OnChanges {
  @Input() data: TableRowData[] = [];
  @Input() metaData: ReportMetaData = {} as ReportMetaData;
  @Input() isLoading: boolean = false;
  @Input() isViewOnly: boolean = false;
  @Input() projectId: string = '';
  @Input() globalAccess: boolean = false;
  @Input() customShouldAnimateRow?: (element: ReportData) => boolean;
  @Input() canDeleteReport: boolean = true;
  @Input() canEditReportName: boolean = true;
  @Input() canOpenReport: boolean = true;
  @Input() canMergeReport: boolean = true;
  @Input() canShareToOriginal: boolean = false;
  @Input() isClonedProject: boolean = false;
  @Input() tableMode: 'default' | 'sharedReports' = 'default';
  @Input() commandScreenModeEnabled: boolean = false;
  @Input() columnsToShow: string[] = [];
  @Input() isNwcClient: boolean = false;
  /** Opt-in manual ordering UI (drag handle column + Move row action). Off keeps today's table. */
  @Input() reorderEnabled = false;
  /** Keeps the drag handle visible but inert; `dragDisabledReason` becomes its tooltip. */
  @Input() dragDisabled = false;
  @Input() dragDisabledReason: string | null = null;
  /**
   * PPE auto-detection column (V2CORE-2901). null (default) = shown when any row carries a
   * `ppe_detection_status` (a backend that knows PPE); true / false forces it.
   */
  @Input() showPpeStatus: boolean | null = null;
  /**
   * Opt-in row checkboxes with a "Select all" header box (V2CORE-3363, bulk "Run AI
   * detection"). Off keeps today's table. The host owns the selection: it passes the ticked ids
   * in `selectedReportIds` and updates them from `rowSelectionToggled` / `pageSelectionToggled`.
   */
  @Input() selectable = false;
  @Input() selectedReportIds: string[] = [];

  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() reportSelected = new EventEmitter<ReportData>();
  @Output() reportDeleted = new EventEmitter<string>();
  @Output() reportUnmerged = new EventEmitter<string>();
  @Output() reportOpened = new EventEmitter<string>();
  @Output() reportShareToOriginal = new EventEmitter<string>();
  @Output() sharedReportOpened = new EventEmitter<SharedReportTableData>();
  @Output() sharedReportCompared = new EventEmitter<SharedReportTableData>();
  @Output() reportDropped = new EventEmitter<ReportDroppedEvent>();
  @Output() moveRequested = new EventEmitter<ReportData>();
  /** A row checkbox was clicked; carries the report id. */
  @Output() rowSelectionToggled = new EventEmitter<string>();
  /** The "Select all" box was clicked; carries the report ids on the page. */
  @Output() pageSelectionToggled = new EventEmitter<string[]>();

  dataSource: MatTableDataSource<TableRowData>;
  defaultColumnsToDisplay = [
    'short_id',
    'report_name',
    'project_number',
    'published_at',
    'creator',
    'report_type',
    'report_status',
    'floors',
    'camera_serial_numbers',
    'actions',
  ];
  sharedReportsColumnsToDisplay = [
    'report_name',
    'source_organization',
    'shared_by_name',
    'shared_at',
    'floors_count',
    'points_count',
    'actions',
  ];
  columnsToDisplay = [...this.defaultColumnsToDisplay];

  REPORT_STATUS = REPORT_STATUS;
  readonly DRAG_HANDLE_COLUMN = DRAG_HANDLE_COLUMN;
  readonly PPE_STATUS_COLUMN = PPE_STATUS_COLUMN;
  readonly SELECT_COLUMN = SELECT_COLUMN;

  constructor(
    private toaster: ToastrService,
    private transloco: TranslocoService,
  ) {
    this.dataSource = new MatTableDataSource<TableRowData>([]);
  }

  ngOnInit(): void {
    if (this.columnsToShow.length > 0) {
      this.columnsToDisplay = this.columnsToShow;
    }
    this.dataSource.data = this.data;
    this.updateColumnsForMode();
    this.updateColumnsForProjectName();
    this.updateColumnsForPpe();
    this.updateColumnsForReorder();
    this.updateColumnsForSelection();
  }

  ngOnChanges(): void {
    if (this.data) {
      this.dataSource.data = this.data;
      this.updateColumnsForMode();
      this.updateColumnsForProjectName();
      this.updateColumnsForPpe();
      this.updateColumnsForReorder();
      this.updateColumnsForSelection();
    }
  }

  isSharedReportsMode(): boolean {
    return this.tableMode === 'sharedReports';
  }

  get emptyStateImageSrc(): string | null {
    return getEmptyStatePlaceholderImage(this.isNwcClient);
  }

  private updateColumnsForMode(): void {
    this.columnsToDisplay = this.isSharedReportsMode()
      ? [...this.sharedReportsColumnsToDisplay]
      : [...this.defaultColumnsToDisplay];
  }

  private updateColumnsForProjectName(): void {
    if (this.isSharedReportsMode()) {
      return;
    }
    // Add project_name column if globalAccess is enabled and data has project_name
    if (
      this.globalAccess &&
      this.data.length > 0 &&
      this.data.some((report) => (report as ReportData).project_name)
    ) {
      if (!this.columnsToDisplay.includes('project_name')) {
        this.columnsToDisplay.splice(2, 0, 'project_name');
      }
    }
  }

  /** Whether the PPE detection column is rendered. */
  isPpeColumnShown(): boolean {
    if (this.isSharedReportsMode()) {
      return false;
    }
    if (this.showPpeStatus !== null && this.showPpeStatus !== undefined) {
      return this.showPpeStatus;
    }
    return (this.data ?? []).some((row) => {
      const status = (row as ReportData)?.ppe_detection_status;
      return status !== undefined && status !== null;
    });
  }

  /** PPE status of a row; unknown / missing shows "Not detected". */
  ppeStatusOf(row: TableRowData): PpeDetectionStatus {
    return normalizePpeStatus((row as ReportData)?.ppe_detection_status);
  }

  /** AI observation count of a row, or null when unknown. */
  ppeCountOf(row: TableRowData): number | null {
    const count = (row as ReportData)?.ppe_ai_observations_count;
    return count === null || count === undefined ? null : count;
  }

  /** Puts the PPE column right after the report status column. */
  private updateColumnsForPpe(): void {
    const index = this.columnsToDisplay.indexOf(PPE_STATUS_COLUMN);
    if (!this.isPpeColumnShown()) {
      if (index !== -1) {
        this.columnsToDisplay.splice(index, 1);
      }
      return;
    }
    if (index !== -1) {
      return;
    }
    const statusIndex = this.columnsToDisplay.indexOf('report_status');
    const at = statusIndex === -1 ? this.columnsToDisplay.length : statusIndex + 1;
    this.columnsToDisplay.splice(at, 0, PPE_STATUS_COLUMN);
  }

  /** Whether the manual-ordering UI (handle column, Move action) is rendered at all. */
  isReorderUiEnabled(): boolean {
    return (
      this.reorderEnabled && !this.isSharedReportsMode() && !this.isViewOnly && !this.globalAccess
    );
  }

  /** Whether the drag handle is rendered but cannot start a drag. */
  isDragInert(): boolean {
    return (
      !this.isReorderUiEnabled() ||
      this.dragDisabled ||
      this.isLoading ||
      (this.data?.length ?? 0) < 2
    );
  }

  /** Tooltip for the handle: the disabled reason, nothing while otherwise inert, else 'Drag Report'. */
  dragHandleTooltip(): string {
    if (this.dragDisabled && this.dragDisabledReason) {
      return this.dragDisabledReason;
    }
    if (this.isDragInert()) {
      return '';
    }
    return this.transloco.translate('Drag Report');
  }

  onReportDrop(event: CdkDragDrop<TableRowData[]>): void {
    if (this.isDragInert()) {
      return;
    }
    const { previousIndex, currentIndex } = event;
    if (previousIndex === currentIndex) {
      return;
    }
    const rows = [...(this.data ?? [])] as ReportData[];
    if (
      previousIndex < 0 ||
      previousIndex >= rows.length ||
      currentIndex < 0 ||
      currentIndex >= rows.length
    ) {
      return;
    }
    const reportId = rows[previousIndex]?.report_id;
    if (!reportId) {
      return;
    }

    moveItemInArray(rows, previousIndex, currentIndex);
    const anchor = computeReorderAnchor(
      rows.map((row) => row.report_id),
      currentIndex,
    );
    if (!anchor) {
      return;
    }

    // Optimistic: show the new order immediately; the host reloads the page from the server.
    this.data = rows;
    this.dataSource.data = rows;
    this.reportDropped.emit({ reportId, ...anchor });
  }

  onMoveRequested(report: TableRowData): void {
    if (!this.isReorderUiEnabled() || this.isLoading) {
      return;
    }
    this.moveRequested.emit(report as ReportData);
  }

  private updateColumnsForReorder(): void {
    const index = this.columnsToDisplay.indexOf(DRAG_HANDLE_COLUMN);
    if (this.isReorderUiEnabled()) {
      if (index === -1) {
        this.columnsToDisplay.unshift(DRAG_HANDLE_COLUMN);
      }
    } else if (index !== -1) {
      this.columnsToDisplay.splice(index, 1);
    }
  }

  /** Whether the row checkboxes are rendered (never on the Shared Reports tab or read-only). */
  isSelectionEnabled(): boolean {
    return this.selectable && !this.isSharedReportsMode() && !this.isViewOnly;
  }

  /** The report ids of the rows on this page. */
  pageReportIds(): string[] {
    return (this.data ?? [])
      .map((row) => (row as ReportData)?.report_id)
      .filter((id): id is string => !!id);
  }

  isRowSelected(row: TableRowData): boolean {
    const id = (row as ReportData)?.report_id;
    return !!id && (this.selectedReportIds ?? []).includes(id);
  }

  isPageSelected(): boolean {
    const ids = this.pageReportIds();
    return ids.length > 0 && ids.every((id) => (this.selectedReportIds ?? []).includes(id));
  }

  isPagePartlySelected(): boolean {
    const ids = this.pageReportIds();
    const ticked = ids.filter((id) => (this.selectedReportIds ?? []).includes(id)).length;
    return ticked > 0 && ticked < ids.length;
  }

  onRowSelectionToggled(row: TableRowData): void {
    const id = (row as ReportData)?.report_id;
    if (!this.isSelectionEnabled() || !id) {
      return;
    }
    this.rowSelectionToggled.emit(id);
  }

  onPageSelectionToggled(): void {
    if (!this.isSelectionEnabled()) {
      return;
    }
    this.pageSelectionToggled.emit(this.pageReportIds());
  }

  /** Puts the checkbox column first (after the drag handle when there is one). */
  private updateColumnsForSelection(): void {
    const index = this.columnsToDisplay.indexOf(SELECT_COLUMN);
    if (!this.isSelectionEnabled()) {
      if (index !== -1) {
        this.columnsToDisplay.splice(index, 1);
      }
      return;
    }
    if (index !== -1) {
      return;
    }
    const at = this.columnsToDisplay[0] === DRAG_HANDLE_COLUMN ? 1 : 0;
    this.columnsToDisplay.splice(at, 0, SELECT_COLUMN);
  }

  onPageChanged(event: PageEvent): void {
    this.pageChanged.emit(event);
  }

  setSelectedReport(report: TableRowData): void {
    if (this.isSharedReportsMode()) {
      return;
    }
    const reportData = report as ReportData;
    if (this.isUnavailableForSync(reportData)) {
      this.showUnavailableSyncedReportWarning();
      return;
    }
    this.handleReportClick(reportData.report_id);
    this.reportSelected.emit(reportData);
  }

  /**
   * Handle report click and update localStorage to remove red-alert
   * @param reportId - The ID of the report that was clicked
   */
  handleReportClick(reportId: string): void {
    const userData = localStorage.getItem('user-data');
    const parsed = JSON.parse(userData);
    const userId = parsed.user_id;
    if (!userId) return;

    const key = `report-row-clicked-${userId}`;
    let clickedRows: string[] = [];
    try {
      clickedRows = JSON.parse(localStorage.getItem(key) || '[]');
    } catch {}

    if (!clickedRows.includes(reportId)) {
      clickedRows.push(reportId);
      localStorage.setItem(key, JSON.stringify(clickedRows));
      // Force row object change to trigger re-render
      this.data = this.data.map((row) =>
        (row as ReportData).report_id === reportId ? { ...row, _clickedAt: Date.now() } : row,
      );
      this.dataSource.data = this.data;
    }
  }

  onDeleteReport(reportId: string): void {
    const report = this.data.find(
      (row) => !this.isSharedReportsMode() && (row as ReportData)?.report_id === reportId,
    ) as ReportData | undefined;
    if (report && this.isUnavailableForSync(report)) {
      this.showUnavailableSyncedReportWarning();
      return;
    }
    this.reportDeleted.emit(reportId);
  }

  onUnmergeReport(reportId: string): void {
    const report = this.data.find(
      (row) => !this.isSharedReportsMode() && (row as ReportData)?.report_id === reportId,
    ) as ReportData | undefined;
    if (report && this.isUnavailableForSync(report)) {
      this.showUnavailableSyncedReportWarning();
      return;
    }
    this.reportUnmerged.emit(reportId);
  }

  onShareToOriginal(reportId: string): void {
    const report = this.data.find(
      (row) => !this.isSharedReportsMode() && (row as ReportData)?.report_id === reportId,
    ) as ReportData | undefined;
    if (report && this.isUnavailableForSync(report)) {
      this.showUnavailableSyncedReportWarning();
      return;
    }
    this.reportShareToOriginal.emit(reportId);
  }

  isReadOnlySyncedReport(report: TableRowData): boolean {
    if (this.isSharedReportsMode() || !this.isClonedProject) {
      return false;
    }
    return Boolean((report as ReportData)?.is_cloned ?? false);
  }

  openReport(reportId: string): void {
    const report = this.data.find(
      (row) => !this.isSharedReportsMode() && (row as ReportData)?.report_id === reportId,
    ) as ReportData | undefined;
    if (report && this.isUnavailableForSync(report)) {
      this.showUnavailableSyncedReportWarning();
      return;
    }
    if (report && !this.hasOpenableFloors(report)) {
      this.showNoFloorsWarning();
      return;
    }
    this.reportOpened.emit(reportId);
  }

  openSharedReport(report: TableRowData, event: Event): void {
    event.stopPropagation();
    if (!this.hasOpenableFloors(report)) {
      this.showNoFloorsWarning();
      return;
    }
    this.sharedReportOpened.emit(report as SharedReportTableData);
  }

  compareSharedReport(report: TableRowData, event: Event): void {
    event.stopPropagation();
    if (!this.hasOpenableFloors(report)) {
      this.showNoFloorsWarning();
      return;
    }
    this.sharedReportCompared.emit(report as SharedReportTableData);
  }

  copyShortId(shortId: string, event: Event): void {
    event.stopPropagation();
    navigator.clipboard
      .writeText(shortId)
      .then(() => {
        this.toaster.success(this.transloco.translate('Report ID copied to clipboard'));
      })
      .catch(() => {
        this.toaster.error(this.transloco.translate('Failed to copy Report ID'));
      });
  }

  /**
   * Check if a report was published today
   * @param publishedAt - The published_at date string
   * @returns boolean - true if published today, false otherwise
   */
  isPublishedToday(publishedAt: string): boolean {
    if (!publishedAt) return false;
    const publishedDate = new Date(publishedAt);
    const today = new Date();
    return (
      publishedDate.getFullYear() === today.getFullYear() &&
      publishedDate.getMonth() === today.getMonth() &&
      publishedDate.getDate() === today.getDate()
    );
  }

  /**
   * Check if the report has an alert (published today)
   * @param element - The report element
   * @returns boolean - true if report has alert, false otherwise
   */
  hasReportAlert(element: TableRowData): boolean {
    if (this.isSharedReportsMode()) {
      return false;
    }
    return this.isPublishedToday((element as ReportData).published_at);
  }

  /**
   * Get the alert type for the report
   * @param element - The report element
   * @returns string - alert type ('warn' for published today)
   */
  getReportAlertType(element: TableRowData): string {
    if (this.isSharedReportsMode()) {
      return '';
    }
    if (this.isPublishedToday((element as ReportData).published_at)) {
      return 'warn';
    }
    return '';
  }

  /**
   * Check if the row should animate for the logged-in user
   * Uses custom function if provided, otherwise returns false
   * @param element - The report element
   * @returns boolean - true if should animate, false otherwise
   */
  shouldAnimateRow(element: TableRowData): boolean {
    if (this.isSharedReportsMode()) {
      return false;
    }
    if (this.customShouldAnimateRow) {
      return this.customShouldAnimateRow(element as ReportData);
    }
    return false;
  }

  isUnavailableForSync(report: TableRowData): boolean {
    if (this.isSharedReportsMode()) {
      return false;
    }
    const reportData = report as ReportData;
    return Boolean(reportData?.is_unavailable_for_sync || reportData?.has_deleted_sync_items);
  }

  hasOpenableFloors(report: TableRowData): boolean {
    if (this.isSharedReportsMode()) {
      return ((report as SharedReportTableData)?.floors_count ?? 0) > 0;
    }

    return ((report as ReportData)?.report_floors?.length ?? 0) > 0;
  }

  private showUnavailableSyncedReportWarning(): void {
    this.toaster.warning(
      this.transloco.translate(
        'This report includes deleted synced floors/points and is unavailable for this action.',
      ),
    );
  }

  private showNoFloorsWarning(): void {
    this.toaster.warning(this.transloco.translate('No Floors'));
  }
}
